import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapFinanceOverviewWorkbench,
  mapFinancePayableOverviewPage,
  mapFinanceReceivableOverviewPage,
  type FinanceOverviewWorkbenchRaw,
  type FinancePayableOverviewPageRaw,
  type FinanceReceivableOverviewPageRaw,
} from "../api/adapters";
import { financePayableOverviewQuery, financeReceivableOverviewQuery } from "../config";
import type {
  FinanceOverviewExportFile,
  FinanceOverviewWorkbench,
  FinancePayableOverviewFilters,
  FinancePayableOverviewResult,
  FinanceReceivableOverviewFilters,
  FinanceReceivableOverviewResult,
} from "../types";

/** 取应收列表的一页：全部有团编码的团，合计算全部筛选结果；超过候选上限时后端报错，不截断。 */
export async function fetchFinanceReceivableOverview(
  filters: FinanceReceivableOverviewFilters,
  page: number,
  pageSize: number,
): Promise<FinanceReceivableOverviewResult> {
  const response = await httpClient.get<ApiEnvelope<FinanceReceivableOverviewPageRaw>>("/finance-overview/receivables", {
    params: { ...financeReceivableOverviewQuery(filters), page, page_size: pageSize },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("应收列表接口返回了无效数据。");
  }
  return mapFinanceReceivableOverviewPage(response.data.data);
}

/** 取应付列表的一页：有确认件或有请款的行程一行，合计算全部筛选结果；超过候选上限时后端报错，不截断。 */
export async function fetchFinancePayableOverview(
  filters: FinancePayableOverviewFilters,
  page: number,
  pageSize: number,
): Promise<FinancePayableOverviewResult> {
  const response = await httpClient.get<ApiEnvelope<FinancePayableOverviewPageRaw>>("/finance-overview/payables", {
    params: { ...financePayableOverviewQuery(filters), page, page_size: pageSize },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("应付列表接口返回了无效数据。");
  }
  return mapFinancePayableOverviewPage(response.data.data);
}

/** 取财务工作台：两张总数卡、三类待办的件数；没有那一页权限的项接口不给，映射成 null。 */
export async function fetchFinanceOverviewWorkbench(): Promise<FinanceOverviewWorkbench> {
  const response = await httpClient.get<ApiEnvelope<FinanceOverviewWorkbenchRaw>>("/finance-overview/workbench");
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("财务工作台接口返回了无效数据。");
  }
  return mapFinanceOverviewWorkbench(response.data.data);
}

/**
 * 取一个导出文件：文件名取响应头里的，读不到（跨域没暴露这个头）或解不开时用兜底名。
 * 出错时原样抛出拦截器归一后的错误（错误体是 Blob、没有错误码），超没超候选上限由上层按 HTTP 状态判。
 */
async function fetchOverviewExport(
  url: string,
  params: Record<string, unknown>,
  fallbackName: string,
): Promise<FinanceOverviewExportFile> {
  const response = await httpClient.get<Blob>(url, { params, responseType: "blob" });
  const disposition = String(response.headers?.["content-disposition"] ?? "");
  const encodedName = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  let fileName = fallbackName;
  if (encodedName) {
    try {
      fileName = decodeURIComponent(encodedName.replace(/^"|"$/g, ""));
    } catch {
      // 解不开时沿用兜底文件名。
    }
  }
  return { blob: response.data, fileName };
}

/** 导出应收列表当前筛选出的全部团（不分页）；超过候选上限时后端报错、不给截断的文件。 */
export function fetchFinanceReceivableOverviewExport(
  filters: FinanceReceivableOverviewFilters,
): Promise<FinanceOverviewExportFile> {
  return fetchOverviewExport(
    "/finance-overview/receivables/export.xlsx",
    financeReceivableOverviewQuery(filters),
    "应收列表.xlsx",
  );
}

/** 导出应付列表当前筛选出的全部行程（不分页、含请款状态）；超过候选上限时后端报错、不给截断的文件。 */
export function fetchFinancePayableOverviewExport(
  filters: FinancePayableOverviewFilters,
): Promise<FinanceOverviewExportFile> {
  return fetchOverviewExport(
    "/finance-overview/payables/export.xlsx",
    financePayableOverviewQuery(filters),
    "应付列表.xlsx",
  );
}
